import EmptyState from "@/components/clubs/empty-state";
import FeatureCard from "@/components/clubs/feature-card";
import InvitationCard from "@/components/clubs/invitation-card";
import { Button } from "@/components/ui/button";
import { SideCannons } from "@/components/ui/confetti";
import { createFileRoute } from "@tanstack/react-router";
import { Search, ShoppingBag, Target, Users } from "lucide-react";
import { useState } from "react";
import {
  useClubInvitations,
  useHandleClubInvitation,
} from "../data/clubs/hooks";
import { usePremiumInterest } from "../data/growth/hooks";

export const Route = createFileRoute("/clubs")({
  component: RouteComponent,
});

// Features data
const features = [
  {
    icon: Users,
    title: "Teams",
    description:
      "Create and manage as many teams as you'd like to engage in friendly competition.",
  },
  {
    icon: Target,
    title: "Missions",
    description:
      "Create engaging and fully customizable fitness challenges to spark friendly competition and keep your club motivated.",
  },
  {
    icon: ShoppingBag,
    title: "Club Shop",
    description:
      "Transform fitness achievements into real rewards! Create custom incentives that members can earn with their points.",
  },
  {
    icon: Search,
    title: "Insights",
    description:
      "Get detailed insights into activities, member trends, and progress with beautiful graphs and analytics.",
  },
];

function RouteComponent() {
  // Club invitations
  const { data: invitations, isLoading, isError } = useClubInvitations();
  const handleInvitation = useHandleClubInvitation();
  const [showThankYou, setShowThankYou] = useState(false);
  const premiumInterestMutation = usePremiumInterest();
  const [showConfetti, setShowConfetti] = useState(false);

  // Book Demo CTA
  const handleBookDemo = () => {
    setShowThankYou(true);
    setShowConfetti(true); // Trigger confetti when booking demo
    premiumInterestMutation.mutate({ featureName: "Book Demo" });
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 font-montserrat">
      {/* Confetti effect */}
      {showConfetti && (
        <SideCannons
          particleCount={80}
          startVelocity={60}
          duration={3000}
          spread={100}
          colors={["#2b7fff", "#fe9a00", "#fff", "#4169E1"]}
          onComplete={() => setShowConfetti(false)}
        />
      )}
      {/* Hero Section */}
      <section className="w-full py-12 md:py-16 bg-gradient-to-br from-[#2b7fff] via-[#2b7fff]/90 to-[#fe9a00]">
        <div className="container px-4 md:px-6">
          <div className="flex flex-col items-center justify-center text-center">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl text-white">
              Pro Clubs
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-white/90 font-light mt-2">
              Unlock the full power of Miles for your group
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="w-full py-16 md:py-24">
        <div className="container px-4 md:px-6">
          <div className="flex flex-col items-center justify-center space-y-4 text-center mb-12">
            <div className="space-y-2">
              <h2 className="text-2xl font-bold tracking-tighter sm:text-3xl md:text-4xl">
                Powerful Features for Your Club
              </h2>
              <p className="mx-auto max-w-[700px] text-gray-500 md:text-lg">
                Everything you need to create an engaging fitness community
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {features.map((feature, idx) => (
              <div
                key={feature.title}
                className="animate-fade-in"
                style={{ animationDelay: `${idx * 150}ms` }}
              >
                <FeatureCard
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                  color={idx % 2 === 0 ? "#2b7fff" : "#fe9a00"}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Club Invitations Section */}
      <section className="w-full py-12 md:py-16 bg-white border-t border-gray-200">
        <div className="container px-4 md:px-6">
          <div className="flex flex-col items-center space-y-4 text-center mb-8">
            <div className="space-y-2">
              <h2 className="text-2xl font-bold tracking-tighter sm:text-3xl">
                Your Club Invitations
              </h2>
              <p className="mx-auto max-w-[700px] text-gray-500">
                Accept or decline invitations to join clubs
              </p>
            </div>
          </div>

          <div className="max-w-3xl mx-auto">
            {isLoading ? (
              <EmptyState
                title="Loading..."
                description="Fetching your club invitations."
              />
            ) : isError ? (
              <EmptyState
                title="Error"
                description="Could not load invitations."
              />
            ) : invitations &&
              invitations.filter((inv) => inv.status === "PENDING").length >
                0 ? (
              <div className="flex flex-col gap-4">
                {invitations
                  .filter((inv) => inv.status === "PENDING")
                  .map((invitation: any) => (
                    <InvitationCard
                      key={invitation.id}
                      clubName={invitation.club.name}
                      icon={Users}
                      inviterName={`${invitation.inviter.firstName} ${invitation.inviter.lastName}`}
                      date={
                        invitation.createdAt
                          ? new Date(invitation.createdAt).toLocaleDateString()
                          : ""
                      }
                      onAccept={() =>
                        handleInvitation.mutate({
                          invitationId: invitation.id,
                          action: "accept",
                        })
                      }
                      onDecline={() =>
                        handleInvitation.mutate({
                          invitationId: invitation.id,
                          action: "decline",
                        })
                      }
                    />
                  ))}
              </div>
            ) : (
              <EmptyState
                title="No invitations"
                description="You don't have any club invitations at the moment. When someone invites you to join a club, it will appear here."
              />
            )}
          </div>
        </div>
      </section>

      {/* CTA Section - Book Demo */}
      <section className="w-full py-16 md:py-20 bg-gradient-to-r from-[#2b7fff] via-[#2b7fff]/90 to-[#fe9a00]">
        <div className="container px-4 md:px-6">
          <div className="flex flex-col items-center justify-center space-y-6 text-center">
            <div className="space-y-3">
              <h2 className="text-2xl font-bold tracking-tighter sm:text-3xl md:text-4xl text-white">
                Ready to Take Your Club to the Next Level?
              </h2>
            </div>
            <div>
              {!showThankYou ? (
                <Button
                  size="lg"
                  className="px-10 py-4 text-lg font-bold rounded-full bg-gradient-to-r from-[#2b7fff] to-[#fe9a00] text-white shadow-lg border-0 hover:opacity-90 transition-all"
                  onClick={handleBookDemo}
                  disabled={premiumInterestMutation.isPending}
                >
                  🎉 Let us know!
                </Button>
              ) : (
                <div className="text-white text-lg font-semibold">
                  Thank you for your interest! You'll receive an email soon.
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
